<template>
  <v-container fluid>

    <!-- Header -->
    <div class="d-flex align-center mb-4" style="gap:12px">
      <v-btn small outlined color="info" tile @click="$router.back()">
        <v-icon left>mdi-arrow-left-bold</v-icon>Volver
      </v-btn>
      <div>
        <div class="text-h6 font-weight-bold">Solicitudes de Anticipo de Módulo</div>
        <div class="caption grey--text">Revisión y aprobación de solicitudes pendientes</div>
      </div>
      <v-spacer />
      <v-btn small outlined :loading="loading" @click="fetchSolicitudes" title="Actualizar">
        <v-icon small>mdi-refresh</v-icon>
      </v-btn>
    </div>

    <!-- Filtro por estado -->
    <div class="d-flex align-center mb-3 flex-wrap" style="gap:8px">
      <span class="caption grey--text font-weight-medium">Filtrar por estado:</span>
      <v-btn-toggle v-model="filtroEstado" mandatory dense rounded color="deep-purple darken-2" @change="onFiltroChange">
        <v-btn value="" x-small>Todos</v-btn>
        <v-btn value="SOLICITADO" x-small>
          <v-icon left x-small>mdi-clock-outline</v-icon>Solicitado
        </v-btn>
        <v-btn value="APROBADO" x-small>
          <v-icon left x-small>mdi-check-circle-outline</v-icon>Aprobado
        </v-btn>
        <v-btn value="RECHAZADO" x-small>
          <v-icon left x-small>mdi-close-circle-outline</v-icon>Rechazado
        </v-btn>
      </v-btn-toggle>
      <v-chip v-if="total > 0" x-small color="deep-purple darken-2" dark class="ml-2">
        {{ total }} registro(s)
      </v-chip>
    </div>

    <v-card outlined>
      <v-progress-linear v-if="loading" indeterminate color="deep-purple darken-2" />

      <v-data-table
        :headers="headers"
        :items="solicitudes"
        :loading="loading"
        :server-items-length="total"
        :options.sync="options"
        :footer-props="{ itemsPerPageOptions: [10, 20, 50] }"
        hide-default-footer
        dense
      >
        <!-- Programa -->
        <template v-slot:item.programa="{ item }">
          <div>
            <v-chip x-small color="deep-purple darken-2" dark label class="mb-1">{{ item.programa_codigo }}</v-chip>
            <div class="caption" style="max-width:220px; white-space:normal; line-height:1.3">
              {{ item.programa_nombre_compuesto }}
            </div>
          </div>
        </template>

        <!-- Sede -->
        <template v-slot:item.sede_nombre="{ item }">
          <div class="caption">{{ item.sede_nombre }}</div>
        </template>

        <!-- Módulo -->
        <template v-slot:item.modulo_objetivo="{ item }">
          <v-chip
            x-small
            :color="item.modulo_objetivo === 0 ? 'deep-purple darken-1' : 'indigo darken-1'"
            dark label
          >
            Módulo {{ item.modulo_objetivo }}
          </v-chip>
        </template>

        <!-- Solicitante -->
        <template v-slot:item.solicitante="{ item }">
          <div class="caption">
            {{ item.solicitante_nombres }} {{ item.solicitante_primer_apellido }} {{ item.solicitante_segundo_apellido }}
          </div>
          <div class="caption grey--text">{{ item.fecha_registro }}</div>
        </template>

        <!-- Descripción -->
        <template v-slot:item.descripcion="{ item }">
          <div class="caption" style="max-width:200px; white-space:normal; line-height:1.3">
            {{ item.descripcion }}
          </div>
        </template>

        <!-- Estado -->
        <template v-slot:item.estado="{ item }">
          <v-chip x-small :color="colorEstado(item.estado)" dark>{{ item.estado }}</v-chip>
          <div class="caption grey--text mt-1" v-if="item.respuesta">
            {{ item.respuesta }}
          </div>
        </template>

        <!-- Acciones -->
        <template v-slot:item.acciones="{ item }">
          <template v-if="esAutorizante && item.estado === 'SOLICITADO'">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn icon small color="green darken-1" v-on="on"
                  :loading="procesando === item.id + '_ap'"
                  :disabled="procesando !== null"
                  @click="abrirAprobar(item)"
                >
                  <v-icon small>mdi-check-circle</v-icon>
                </v-btn>
              </template>
              <span>Aprobar solicitud</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn icon small color="red darken-1" v-on="on"
                  :loading="procesando === item.id + '_re'"
                  :disabled="procesando !== null"
                  @click="abrirRechazar(item)"
                >
                  <v-icon small>mdi-close-circle</v-icon>
                </v-btn>
              </template>
              <span>Rechazar solicitud</span>
            </v-tooltip>
          </template>
          <span v-else class="caption grey--text">—</span>
        </template>

        <!-- Sin datos -->
        <template v-slot:no-data>
          <div class="py-8 text-center grey--text">
            <v-icon large color="grey lighten-2">mdi-rocket-outline</v-icon>
            <div class="mt-2 caption">Sin solicitudes{{ filtroEstado ? ' en estado ' + filtroEstado : '' }}.</div>
          </div>
        </template>
      </v-data-table>

      <!-- Paginación manual -->
      <div v-if="total > 0" class="d-flex align-center justify-end pa-3" style="gap:8px; border-top:1px solid #e0e0e0">
        <span class="caption grey--text">
          {{ (options.page - 1) * options.itemsPerPage + 1 }}–{{ Math.min(options.page * options.itemsPerPage, total) }} de {{ total }}
        </span>
        <v-btn icon x-small :disabled="options.page <= 1" @click="options.page--">
          <v-icon small>mdi-chevron-left</v-icon>
        </v-btn>
        <v-btn icon x-small :disabled="options.page * options.itemsPerPage >= total" @click="options.page++">
          <v-icon small>mdi-chevron-right</v-icon>
        </v-btn>
      </div>
    </v-card>

    <!-- Dialog aprobar -->
    <v-dialog v-model="dialogAprobar" max-width="420" persistent>
      <v-card>
        <v-card-title class="py-4 px-5">
          <v-icon color="green darken-1" class="mr-2">mdi-check-circle-outline</v-icon>
          <span class="subtitle-1 font-weight-bold">Aprobar solicitud</span>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4 px-5 pb-3" v-if="itemSeleccionado">
          <div class="caption grey--text mb-2">
            Programa: <strong>{{ itemSeleccionado.programa_nombre_compuesto }}</strong>
          </div>
          <div class="d-flex align-center pa-3 rounded mb-2" style="background:#e8f5e9; border-left:3px solid #43a047">
            <v-icon color="green darken-2" class="mr-2">mdi-rocket-launch-outline</v-icon>
            <div>
              <div class="overline" style="font-size:9px;color:#388e3c">Anticipo solicitado</div>
              <div class="body-2 font-weight-bold green--text text--darken-2">
                Módulo {{ itemSeleccionado.modulo_objetivo }}
              </div>
            </div>
          </div>
          <div class="caption grey--text" v-if="itemSeleccionado.descripcion">
            Justificación: {{ itemSeleccionado.descripcion }}
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions class="px-5 py-3">
          <v-spacer />
          <v-btn text small @click="dialogAprobar = false" :disabled="procesando !== null">Cancelar</v-btn>
          <v-btn color="green darken-1" dark small
            :loading="procesando !== null"
            @click="confirmarAprobar"
          >
            <v-icon left small>mdi-check</v-icon>Confirmar aprobación
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog rechazar -->
    <v-dialog v-model="dialogRechazar" max-width="420" persistent>
      <v-card>
        <v-card-title class="py-4 px-5">
          <v-icon color="red darken-1" class="mr-2">mdi-close-circle-outline</v-icon>
          <span class="subtitle-1 font-weight-bold">Rechazar solicitud</span>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4 px-5 pb-1" v-if="itemSeleccionado">
          <div class="caption grey--text mb-3">
            Programa: <strong>{{ itemSeleccionado.programa_nombre_compuesto }}</strong><br>
            Módulo: <strong>Módulo {{ itemSeleccionado.modulo_objetivo }}</strong>
          </div>
          <v-textarea
            v-model="motivoRechazo"
            label="Motivo del rechazo *"
            outlined dense rows="3"
            placeholder="Indica el motivo por el que se rechaza la solicitud..."
          />
        </v-card-text>
        <v-divider />
        <v-card-actions class="px-5 py-3">
          <v-spacer />
          <v-btn text small @click="dialogRechazar = false" :disabled="procesando !== null">Cancelar</v-btn>
          <v-btn color="red darken-1" dark small
            :loading="procesando !== null"
            :disabled="!motivoRechazo.trim()"
            @click="confirmarRechazar"
          >
            <v-icon left small>mdi-close</v-icon>Rechazar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" top right :timeout="4000">
      {{ snackbar.msg }}
      <template v-slot:action>
        <v-btn text small @click="snackbar.show = false">OK</v-btn>
      </template>
    </v-snackbar>

  </v-container>
</template>

<script>
import xaxios from '@/xaxios';

const API = process.env.VUE_APP_API_REST;

export default {
  name: 'SolicitudesAnticipoModuloListado',

  data() {
    return {
      solicitudes: [],
      total: 0,
      loading: false,
      procesando: null,
      filtroEstado: 'SOLICITADO',
      options: { page: 1, itemsPerPage: 20 },
      dialogAprobar: false,
      dialogRechazar: false,
      itemSeleccionado: null,
      motivoRechazo: '',
      snackbar: { show: false, msg: '', color: 'success' },
      headers: [
        { text: 'ID',          value: 'id',              sortable: false, width: '50px' },
        { text: 'Programa',    value: 'programa',        sortable: false },
        { text: 'Sede',        value: 'sede_nombre',     sortable: false, width: '110px' },
        { text: 'Módulo',      value: 'modulo_objetivo', sortable: false, width: '90px' },
        { text: 'Descripción', value: 'descripcion',     sortable: false },
        { text: 'Solicitante', value: 'solicitante',     sortable: false },
        { text: 'Estado',      value: 'estado',          sortable: false, width: '110px' },
        { text: 'Acciones',    value: 'acciones',        sortable: false, width: '90px' },
      ],
    };
  },

  computed: {
    esAutorizante() {
      const cargo = this.$store.state.CARGO;
      return cargo && [1, 5, 26].includes(cargo.id);
    },
  },

  watch: {
    'options.page'() { this.fetchSolicitudes(); },
    'options.itemsPerPage'() { this.options.page = 1; this.fetchSolicitudes(); },
  },

  methods: {
    onFiltroChange() {
      this.options.page = 1;
      this.fetchSolicitudes();
    },

    async fetchSolicitudes() {
      this.loading = true;
      try {
        const cargos = this.$store.state.CARGOS || [];
        const params = {
          page: this.options.page,
          per_page: this.options.itemsPerPage,
          sedes_ids: cargos.length ? [...new Set(cargos.map(c => c.sede?.id).filter(Boolean))].join(',') : undefined,
        };
        if (this.filtroEstado) params.estado = this.filtroEstado;

        const { data } = await xaxios.get(
          `${API}/api/v1/anticipo-modulo/solicitudes`,
          { params }
        );
        this.solicitudes = data.data?.data || [];
        this.total = data.data?.total || 0;
      } catch {
        this.solicitudes = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },

    abrirAprobar(item) {
      this.itemSeleccionado = item;
      this.dialogAprobar = true;
    },

    async confirmarAprobar() {
      this.procesando = this.itemSeleccionado.id + '_ap';
      try {
        await xaxios.patch(
          `${API}/api/v1/anticipo-modulo/solicitudes/${this.itemSeleccionado.id}`,
          { estado: 'APROBADO' }
        );
        this.notificar('Solicitud aprobada correctamente', 'success');
        this.dialogAprobar = false;
        this.itemSeleccionado = null;
        await this.fetchSolicitudes();
      } catch (e) {
        const msg = e?.response?.status === 403
          ? 'No tienes permiso para aprobar solicitudes'
          : e?.response?.data?.error || e?.response?.data?.message || 'Error al aprobar';
        this.notificar(msg, 'error');
      } finally {
        this.procesando = null;
      }
    },

    abrirRechazar(item) {
      this.itemSeleccionado = item;
      this.motivoRechazo = '';
      this.dialogRechazar = true;
    },

    async confirmarRechazar() {
      if (!this.motivoRechazo.trim()) return;
      this.procesando = this.itemSeleccionado.id + '_re';
      try {
        await xaxios.patch(
          `${API}/api/v1/anticipo-modulo/solicitudes/${this.itemSeleccionado.id}`,
          { estado: 'RECHAZADO', respuesta: this.motivoRechazo }
        );
        this.notificar('Solicitud rechazada', 'warning');
        this.dialogRechazar = false;
        this.itemSeleccionado = null;
        await this.fetchSolicitudes();
      } catch (e) {
        const msg = e?.response?.status === 403
          ? 'No tienes permiso para rechazar solicitudes'
          : e?.response?.data?.error || e?.response?.data?.message || 'Error al rechazar';
        this.notificar(msg, 'error');
      } finally {
        this.procesando = null;
      }
    },

    colorEstado(estado) {
      const map = { SOLICITADO: 'orange darken-2', APROBADO: 'green darken-2', RECHAZADO: 'red darken-1' };
      return map[estado] || 'grey';
    },

    notificar(msg, color = 'success') {
      this.snackbar = { show: true, msg, color };
    },
  },

  mounted() {
    this.fetchSolicitudes();
  },
};
</script>
